/**
 * Cliente HTTP centralizado para toda la aplicación
 * Configuración unificada de axios con interceptores
 */
import axios from 'axios';

// Configuración centralizada de API
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  TIMEOUT: {
    DEFAULT: parseInt(process.env.REACT_APP_EXTRACTION_TIMEOUT_DEFAULT, 10) || 300000,
    ZIP: parseInt(process.env.REACT_APP_EXTRACTION_TIMEOUT_ZIP, 10) || 900000,
  },
};

// Cliente HTTP base
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT.DEFAULT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token de autenticación
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de respuesta
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Evitar redirigir automáticamente en las llamadas de auth
      // para que la UI (p.ej. formulario de login) pueda mostrar mensajes
      const requestBase = error.config?.baseURL || '';
      const requestUrl = error.config?.url || '';
      const isAuthRequest = requestBase.includes('/auth') || requestUrl.includes('/login') || requestUrl.includes('/register') || requestUrl.includes('/me');

      if (!isAuthRequest) {
        // Token expirado o inválido para llamadas protegidas -> forzar login
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Crear clientes especializados para diferentes módulos
export const createApiClient = (baseURL, timeout = API_CONFIG.TIMEOUT.DEFAULT) => {
  const client = axios.create({
    baseURL: `${API_CONFIG.BASE_URL}${baseURL}`,
    timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Aplicar los mismos interceptores
  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        const requestBase = error.config?.baseURL || '';
        const requestUrl = error.config?.url || '';
        const isAuthRequest = requestBase.includes('/auth') || requestUrl.includes('/login') || requestUrl.includes('/register') || requestUrl.includes('/me');

        if (!isAuthRequest) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }
  );

  return client;
};

// Clientes especializados por módulo
export const apiClients = {
  auth: createApiClient('/api/v1/auth'),
  extraction: createApiClient('/api/v1/extraction'),
  documentConfig: createApiClient('/api/v1/document-config'),
  users: createApiClient('/api/v1/users'),
  pricing: createApiClient('/api/v1/pricing'),
  contact: createApiClient('/api/v1/contact'),
};

export default apiClient;
